import { Card, Empty, Pagination, Spin, Typography } from "antd";
import React from "react";

const { Text } = Typography;

interface MobileTableProps<T extends { id: number }> {
  columns: Array<{
    title: string;
    dataIndex: keyof T | string;
    key: string;
    render?: (value: unknown, record: T, index: number) => React.ReactNode;
  }>;
  onClick: (id: number, record: T) => void;
  loading: boolean;
  data: T[];
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, pageSize: number) => void;
  };
  emptyText?: string;
  actions?: (record: T) => React.ReactNode;
}

const MobileTable = <T extends { id: number }>({
  columns,
  data,
  pagination,
  onClick,
  loading,
  emptyText,
  actions,
}: MobileTableProps<T>) => {
  return (
    <div style={{ width: "100%", position: "relative" }}>
      {loading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <Spin size="large" />
        </div>
      )}
      {data.map(record => (
        <Card
          key={record.id}
          style={{ marginBottom: "16px", width: "100%" }}
          onClick={() => onClick(record.id, record)}
        >
          {actions && (
            <div
              role="presentation"
              style={{ position: "absolute", top: "12px", right: "12px" }}
              onClick={e => e.stopPropagation()}
            >
              {actions(record)}
            </div>
          )}
          {columns.map(column => {
            if (column.key === "actions") return null;

            const value =
              typeof column.dataIndex === "string" && column.dataIndex in record
                ? record[column.dataIndex as keyof T]
                : undefined;

            return (
              <div key={column.key} style={{ marginBottom: "8px" }}>
                <Text strong>{column.title}:</Text>
                <div>
                  {column.render ? (
                    column.render(value, record, data.indexOf(record))
                  ) : (
                    <Text>{String(value ?? "")}</Text>
                  )}
                </div>
              </div>
            );
          })}
        </Card>
      ))}
      {data.length === 0 && (
        <div style={{ marginTop: "16px", display: "flex", justifyContent: "center" }}>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText ?? "Ingen data"} />
        </div>
      )}
      {pagination && (
        <div style={{ marginTop: "16px", display: "flex", justifyContent: "center" }}>
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={pagination.onChange}
          />
        </div>
      )}
    </div>
  );
};

export default MobileTable;
